import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone } from "lucide-react";
import Card from "../common/Card";
import { useToast } from "../../hooks/useToast";
import TapCardShared from "./LeadFormSteps/TapCard";
import StepTrade from "./LeadFormSteps/StepTrade";
import StepProjectType from "./LeadFormSteps/StepProjectType";
import StepTradeDetails from "./LeadFormSteps/StepTradeDetails";
import StepContactDetails from "./LeadFormSteps/StepContactDetails";
import StepPhotos from "./LeadFormSteps/StepPhotos";
import StepReview from "./LeadFormSteps/StepReview";

const MAX_FILES = 8;
const TOTAL_STEPS = 6;

const STEP_NAMES = [
  "What do you need done?",
  "What kind of job is it?",
  "Job details",
  "Your details",
  "Add photos",
  "Review & submit",
];

// ─── TapCard (re-exported from shared file for backward compat) ──────────────
export { TapCardShared as TapCard };

// ─── Legacy trade options (kept for fallback) ─────────────────────────────────
// Canonical trade list now lives in src/config/tradeOptions.js.
// Re-exported here for back-compat with existing imports.
export { TRADE_PROJECT_TYPES as tradeOptions } from "../../config/tradeOptions";

// ─── BLANK_FORM ───────────────────────────────────────────────────────────────
const BLANK_FORM = {
  fullName: "",
  phone: "",
  email: "",
  address: "",
  suburb: "",
  state: "",
  postcode: "",
  trade: "",
  projectType: "",
  propertyType: "House",
  areas: "",
  condition: "Fair",
  surfaces: [],
  measurements: "",
  notes: "",
  urgency: "Standard",
  accessDetails: "",
  serviceCategory: "",
  issueType: "",
  uploadedPhotos: [],
  budgetRange: "",
  whenNeeded: "",
  tradeSpecificAnswers: {},
};

const TRUST_SIGNALS = [
  { icon: "🔒", text: "Your contact details are only shared with tradies you approve." },
  { icon: "⚡", text: "Most tradies respond within a few hours." },
  { icon: "🏆", text: "All tradies are reviewed before being listed." },
];

// ─── Checkmark SVG ─────────────────────────────────────────────────────────
function CheckMark() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path d="M2.5 7.5L6 11L12.5 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Progress dots ─────────────────────────────────────────────────────────
// Customer-facing component — intentionally uses hardcoded brand orange (#FF8A00)
// rather than CSS theme variables, as the theme system only applies to tradie views.
function ProgressDots({ current, total }) {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: i <= current ? "#FF8A00" : "var(--border, #e5e7eb)",
            transition: "background 0.2s",
          }}
        />
      ))}
    </div>
  );
}

// ─── File helpers ──────────────────────────────────────────────────────────
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function filesToDataUrls(fileList) {
  const files = Array.from(fileList).slice(0, MAX_FILES);
  return Promise.all(
    files.map(async (file) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      dataUrl: file.type.startsWith("video/")
        ? URL.createObjectURL(file)
        : await fileToDataUrl(file),
    }))
  );
}

// ─── Urgency mapping for scoring compatibility ─────────────────────────────
const urgencyMap = {
  "Yes — water is leaking now": "Emergency",
  "Urgent — needs fixing soon": "Urgent",
  "Can wait — book it in": "Standard",
  "Yes — could be dangerous": "Emergency",
  "ASAP": "Emergency",
  "Within 2 weeks": "Urgent",
  "Within a month": "Standard",
  "Just getting quotes": "Flexible",
  "Whenever": "Flexible",
};

// ─── LeadForm ──────────────────────────────────────────────────────────────
export default function LeadForm({
  session = null,
  onSubmitRequest,
  onBack,
  prefillName = "",
  prefillEmail = "",
  tradieId = null,
  // Per-tradie direct-link customisation. Optional. Honoured shape:
  //   { photos_enabled?: boolean }
  directLinkConfig = null,
  // Direct-link only — when the tradie has 1 trade, skip step 0 entirely
  // and pre-fill it. Pass `business?.trade_types` from DirectQuotePage.
  restrictedTrades = null,
}) {
  const { showToast } = useToast();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Photo step is on by default; tradies can disable it via direct_link_config.
  const photosEnabled = directLinkConfig?.photos_enabled !== false;
  // Direct-link auto-skip: if the tradie has exactly one trade configured, we
  // pre-set form.trade and start the form on step 1 instead of step 0.
  const lockedTrade =
    Array.isArray(restrictedTrades) && restrictedTrades.length === 1
      ? restrictedTrades[0]
      : null;
  // Effective step count drops both the photos step (when disabled) and the
  // trade-picker step (when locked to a single trade).
  const effectiveTotalSteps =
    TOTAL_STEPS - (photosEnabled ? 0 : 1) - (lockedTrade ? 1 : 0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const [form, setForm] = useState(() => ({
    ...BLANK_FORM,
    fullName: prefillName,
    email: prefillEmail,
    trade: lockedTrade || "",
  }));

  // If the tradie has exactly one trade, skip the trade-picker step.
  useEffect(() => {
    if (lockedTrade && step === 0) setStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedTrade]);

  // Bug 5: errors state passed to step components for inline error display
  const [errors] = useState({});

  // Generic field updater
  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  // Photo helpers
  async function handlePhotoUpload(event) {
    const files = event.target.files;
    if (!files || !files.length) return;
    try {
      const newPhotos = await filesToDataUrls(files);
      setForm((prev) => {
        const combined = [...prev.uploadedPhotos, ...newPhotos].slice(0, MAX_FILES);
        return { ...prev, uploadedPhotos: combined };
      });
      event.target.value = "";
    } catch (error) {
      showToast("Failed to process images.", "error");
    }
  }

  function removePhoto(index) {
    setForm((prev) => ({
      ...prev,
      uploadedPhotos: prev.uploadedPhotos.filter((_, i) => i !== index),
    }));
  }

  // Per-step validation
  // Bug 1: overrideValue lets auto-advance steps pass the just-selected value
  //        before React state has flushed, avoiding stale closure failures.
  // Bug 4: case 4 removed — StepContactDetails validates itself before calling onNext.
  function validateStep(stepIndex, overrideValue) {
    switch (stepIndex) {
      case 0:
        if (!(overrideValue || form.trade)) { showToast("Please select a trade.", "error"); return false; }
        return true;
      case 1:
        if (!(overrideValue || form.projectType)) { showToast("Please select a project type.", "error"); return false; }
        return true;
      case 2:
        if (form.trade === "Handyman" && !(form.tradeSpecificAnswers?.handyNotes || "").trim()) {
          showToast("Please describe what needs doing.", "error");
          return false;
        }
        return true;
      default:
        return true;
    }
  }

  // Bug 1: accepts optional overrideValue for steps that auto-advance on selection
  function goNext(overrideValue) {
    if (!validateStep(step, overrideValue)) return;
    setStep((s) => {
      let next = s + 1;
      // Photos step is index 4 in the new numbering; skip when disabled.
      if (!photosEnabled && next === 4) next = 5;
      return Math.min(next, TOTAL_STEPS - 1);
    });
  }

  function goBack() {
    // Locked-trade direct-link customers should never see step 0.
    if (lockedTrade && step === 1) {
      if (onBack) onBack();
      return;
    }
    if (step === 0 && onBack) { onBack(); return; }
    setStep((s) => {
      let prev = s - 1;
      if (!photosEnabled && prev === 4) prev = 3;
      return Math.max(prev, 0);
    });
  }

  // Bug 6: full mapping of tradeSpecificAnswers to legacy Supabase fields
  function buildRequest() {
    const tsa = form.tradeSpecificAnswers || {};

    // Urgency
    const mappedUrgency =
      urgencyMap[tsa.plumbUrgency] ||
      urgencyMap[tsa.elecSafety] ||
      urgencyMap[form.whenNeeded] ||
      "Standard";

    // Areas — cover all 8 trades
    const areasParts = [];
    if (tsa.paintWhat) areasParts.push(tsa.paintWhat);
    if (tsa.paintRooms) areasParts.push(tsa.paintRooms);
    if (tsa.plumbArea) areasParts.push(tsa.plumbArea);
    if (tsa.elecArea) areasParts.push(tsa.elecArea);
    if (tsa.carpType) areasParts.push(tsa.carpType);
    if (tsa.landSize) areasParts.push(tsa.landSize);
    if (tsa.roofStoreys) areasParts.push(tsa.roofStoreys);
    if (tsa.cleanBeds) areasParts.push(tsa.cleanBeds);
    if (tsa.cleanBaths) areasParts.push(`${tsa.cleanBaths} bathrooms`);
    if (tsa.handyCount) areasParts.push(tsa.handyCount);
    if (tsa.handyNotes) areasParts.push(tsa.handyNotes);
    const areas = areasParts.join(", ") || form.areas;

    // Condition
    let condition = form.condition;
    if (tsa.paintCondition) {
      if (tsa.paintCondition.includes("Good")) condition = "Excellent";
      else if (tsa.paintCondition.includes("Fair")) condition = "Fair";
      else if (tsa.paintCondition.includes("Poor")) condition = "Poor";
    }
    if (tsa.roofType && tsa.roofType.toLowerCase().includes("old")) condition = "Poor";

    // Access details — genuine physical access only (vehicle access, site constraints)
    const accessParts = [];
    if (tsa.landAccess) accessParts.push(tsa.landAccess);
    const accessDetails = accessParts.join("; ") || form.accessDetails;

    // Service category (electrical)
    const serviceCategory = tsa.elecBoard || tsa.elecSafety || form.serviceCategory;

    // Issue type (plumbing)
    const issueType = tsa.plumbUrgency || tsa.plumbArea || form.issueType;

    // Notes — customer free-text answers first, then "Job details:" block for structured answers
    const noteParts = [];
    if (tsa.paintNotes) noteParts.push(tsa.paintNotes);
    if (tsa.plumbNotes) noteParts.push(tsa.plumbNotes);
    if (tsa.elecNotes) noteParts.push(tsa.elecNotes);
    if (tsa.carpNotes) noteParts.push(tsa.carpNotes);
    if (tsa.landNotes) noteParts.push(tsa.landNotes);
    if (tsa.roofNotes) noteParts.push(tsa.roofNotes);

    // Structured tap-card answers that don't map to dedicated fields go into "Job details:"
    // Supply preferences and slope belong here, NOT in accessDetails
    const extraEntries = [
      tsa.paintSupply && `Paint supply: ${tsa.paintSupply}`,
      tsa.paintCondition && `Condition: ${tsa.paintCondition}`,
      tsa.plumbAge && `Property age: ${tsa.plumbAge}`,
      tsa.elecBoard && `Switchboard: ${tsa.elecBoard}`,
      tsa.carpSupply && `Materials: ${tsa.carpSupply}`,
      tsa.landSlope && `Slope: ${tsa.landSlope}`,
      tsa.roofType && `Roof type: ${tsa.roofType}`,
      tsa.roofInspect && `Last inspected: ${tsa.roofInspect}`,
      tsa.handySupply && `Materials: ${tsa.handySupply}`,
      tsa.cleanExtras && tsa.cleanExtras.length > 0 && `Cleaning extras: ${tsa.cleanExtras.join(", ")}`,
      tsa.cleanLast && `Last professionally cleaned: ${tsa.cleanLast}`,
    ].filter(Boolean);
    if (extraEntries.length > 0) noteParts.push(`Job details: ${extraEntries.join("; ")}`);

    const notes = noteParts.join("\n") || form.notes;

    // Score
    let score = 40;
    if (form.phone && form.phone.trim()) score += 10;
    if (form.email && form.email.trim()) score += 10;
    if (form.uploadedPhotos.length > 0) score += 20;
    if (form.measurements && form.measurements.trim()) score += 15;
    if (form.address && form.address.trim()) score += 10;
    if (mappedUrgency === "Urgent" || mappedUrgency === "Emergency") score += 15;
    if (condition === "Poor") score += 5;
    score = Math.min(score, 99);

    return {
      id: `RQ-${Date.now()}`,
      customer: form.fullName || "Unnamed customer",
      suburb: form.suburb || "Unknown suburb",
      trade: form.trade,
      type: form.projectType,
      status: "New request",
      score,
      areas,
      complexity: condition,
      budget: form.budgetRange || null,
      photos: form.uploadedPhotos.length,
      lastUpdated: "Just now",
      form: {
        ...form,
        urgency: mappedUrgency,
        areas,
        condition,
        accessDetails,
        serviceCategory,
        issueType,
        notes,
      },
      estimate: null,
      quote: null,
      createdAt: new Date().toISOString(),
      directBusinessId: tradieId || null,
    };
  }

  // opts.freshCustomerUserId is set by StepReview after it signs the customer
  // up (or signs them in) immediately before submission. We stash it on the
  // request object so App.handleSubmitRequest can (a) attach customer_user_id
  // to the insert without waiting for useAuth to propagate, and (b) route the
  // customer to their dashboard rather than the landing page afterwards.
  async function handleSubmit(opts = {}) {
    if (!form.fullName || !form.fullName.trim()) {
      showToast("Please enter your full name before submitting.", "error");
      return;
    }
    if (!form.suburb || !form.suburb.trim()) {
      showToast("Please enter your suburb before submitting.", "error");
      return;
    }
    // Require at least one contact method so the tradie can follow up.
    if (!form.phone.trim() && !form.email.trim()) {
      showToast("Please enter your mobile number or email so the tradie can contact you.", "error");
      return;
    }
    // Validate email format if provided.
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      showToast("Please enter a valid email address.", "error");
      return;
    }
    // Validate phone format if provided — allows digits, spaces, +, -, brackets, 6–20 chars.
    if (form.phone.trim() && !/^[\d\s+\-()]{6,20}$/.test(form.phone.trim())) {
      showToast("Please enter a valid phone number.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const request = buildRequest();
      // Non-enumerable-ish hint for the App submit handler — ignored by the
      // Supabase insert since submitLead only reads the known top-level shape.
      if (opts && opts.freshCustomerUserId) {
        request._customerUserIdOverride = opts.freshCustomerUserId;
      }
      const result = await onSubmitRequest(request);
      // Only celebrate if the submit actually landed. submitLead returns
      // { ok: false, error } on constraint violations / RLS rejects — we
      // must NOT fire the green toast in that case. The caller already
      // surfaced an error toast.
      if (result && result.ok === false) {
        return;
      }
      setForm({ ...BLANK_FORM, fullName: prefillName, email: prefillEmail });
      setStep(0);
      showToast("Quote request submitted successfully.", "success");
    } catch (err) {
      showToast("Something went wrong. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  const stepName = STEP_NAMES[step] || "";

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .lead-form-sidebar { display: none !important; }
          .lead-form-main { grid-column: 1 / -1 !important; }
        }
      `}</style>

      <div className="two-grid">
        {/* ── Sidebar ──────────────────────────────────────────────────── */}
        <Card className="lead-form-sidebar">
          <h2>
            <Phone size={18} /> Request a quote
          </h2>

          <div className="step-list">
            {STEP_NAMES.map((label, index) => {
              const isDone = index < step;
              const isActive = index === step;
              return (
                <div key={label} className={`step-item${isActive ? " active" : ""}`}>
                  <div className={`step-number${isDone ? " done" : ""}`}>
                    {isDone ? <CheckMark /> : index + 1}
                  </div>
                  <div>{label}</div>
                </div>
              );
            })}
          </div>

          <div className="trust-signals">
            {TRUST_SIGNALS.map((t) => (
              <div key={t.text} className="trust-item">
                <span className="trust-icon">{t.icon}</span>
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* ── Step content ───────────────────────────────────────────── */}
        <Card className="lead-form-main">
          {/* Progress bar */}
          <div style={{ marginBottom: "24px" }}>
            <p style={{ fontSize: "0.85rem", color: "var(--muted, #6b7280)", margin: "0 0 8px" }}>
              Step {(() => {
                // Display index = step (1-based), minus locked-trade skip,
                // minus photo skip when later-step + photos disabled.
                let display = step + 1;
                if (lockedTrade) display -= 1;
                if (!photosEnabled && step >= 4) display -= 1;
                return Math.max(1, display);
              })()} of {effectiveTotalSteps} — {stepName}
            </p>
            <ProgressDots
              current={(() => {
                let dot = step;
                if (lockedTrade) dot -= 1;
                if (!photosEnabled && step >= 4) dot -= 1;
                return Math.max(0, dot);
              })()}
              total={effectiveTotalSteps}
            />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 15 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -15 }}
            >
              {step === 0 && (
                <StepTrade form={form} update={update} onNext={goNext} />
              )}
              {step === 1 && (
                <StepProjectType form={form} update={update} onNext={goNext} />
              )}
              {step === 2 && (
                <StepTradeDetails form={form} update={update} onNext={goNext} errors={errors} />
              )}
              {step === 3 && (
                <StepContactDetails form={form} update={update} onNext={goNext} errors={errors} />
              )}
              {step === 4 && (
                <StepPhotos
                  form={form}
                  handlePhotoUpload={handlePhotoUpload}
                  removePhoto={removePhoto}
                  onNext={goNext}
                  onSkip={goNext}
                />
              )}
              {step === 5 && (
                <StepReview
                  form={form}
                  session={session}
                  onSubmit={handleSubmit}
                  submitting={submitting}
                />
              )}

              {/* Back navigation row */}
              <div style={{ marginTop: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                <button
                  type="button"
                  onClick={goBack}
                  disabled={step === 0 && !onBack}
                  style={{
                    background: "none",
                    border: "2px solid var(--border, #e5e7eb)",
                    borderRadius: "12px",
                    padding: "10px 18px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    cursor: step === 0 && !onBack ? "not-allowed" : "pointer",
                    color: step === 0 && !onBack ? "var(--muted, #6b7280)" : "var(--text, #1C1C1E)",
                    opacity: step === 0 && !onBack ? 0.4 : 1,
                  }}
                >
                  ← Back
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </Card>
      </div>
    </>
  );
}
